import type { MatchProfile } from "./types";
import { DEMO_ANSWERS } from "./demoAnswers";

/**
 * Portrait URLs — Unsplash License (https://unsplash.com/license).
 * Cropped for full-bleed match planes (portrait).
 */
const PHOTO = {
  miriam:
    "https://images.unsplash.com/photo-1494790108377-be9c29b29330?auto=format&fit=crop&w=900&h=1200&q=80",
  grace:
    "https://images.unsplash.com/photo-1438761681033-6461ffad8d80?auto=format&fit=crop&w=900&h=1200&q=80",
  elena:
    "https://images.unsplash.com/photo-1534528741775-53994a69daeb?auto=format&fit=crop&w=900&h=1200&q=80",
  hannah:
    "https://images.unsplash.com/photo-1531746020798-e6953c6e8e04?auto=format&fit=crop&w=900&h=1200&q=80",
  ruth:
    "https://images.unsplash.com/photo-1580489944761-15a19d654956?auto=format&fit=crop&w=900&h=1200&q=80",
  lydia:
    "https://images.unsplash.com/photo-1487412720507-e7ab37603c6f?auto=format&fit=crop&w=900&h=1200&q=80",
  sarah:
    "https://images.unsplash.com/photo-1544005313-94ddf0286df2?auto=format&fit=crop&w=900&h=1200&q=80",
  naomi:
    "https://images.unsplash.com/photo-1531123897727-8f129e1688ce?auto=format&fit=crop&w=900&h=1200&q=80",
  daniel:
    "https://images.unsplash.com/photo-1500648767791-00dcc994a43e?auto=format&fit=crop&w=900&h=1200&q=80",
  nathan:
    "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?auto=format&fit=crop&w=900&h=1200&q=80",
  caleb:
    "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?auto=format&fit=crop&w=900&h=1200&q=80",
  james:
    "https://images.unsplash.com/photo-1519085360753-af0119f7cbe7?auto=format&fit=crop&w=900&h=1200&q=80",
  isaac:
    "https://images.unsplash.com/photo-1506794778202-cad84cf45f1d?auto=format&fit=crop&w=900&h=1200&q=80",
  peter:
    "https://images.unsplash.com/photo-1463453091185-61582044d556?auto=format&fit=crop&w=900&h=1200&q=80",
  thomas:
    "https://images.unsplash.com/photo-1492562080023-ab3db95bfbce?auto=format&fit=crop&w=900&h=1200&q=80",
  andrew:
    "https://images.unsplash.com/photo-1504257432389-52343af06ae3?auto=format&fit=crop&w=900&h=1200&q=80",
} as const;

/** Prototype pool — opposite sex is filtered at runtime. */
const RAW_PROFILES: MatchProfile[] = [
  {
    id: "cand_f_1",
    firstName: "Miriam",
    age: 28,
    city: "Austin",
    gender: "female",
    profession: "Pediatric nurse",
    aboutYou:
      "Quiet mornings, church on Sunday, and long walks. I value steadiness over spectacle.",
    lookingFor: "A man who keeps his word and leads with humility.",
    hobbies: "Hiking, choir, cooking for friends",
    scales: { loyalty: 9, honesty: 8, passion: 7, commitment: 9 },
    photoUrl: PHOTO.miriam,
    likedYou: true,
    plan: "vip",
  },
  {
    id: "cand_f_2",
    firstName: "Grace",
    age: 31,
    city: "Nashville",
    gender: "female",
    profession: "Teacher",
    aboutYou: "I teach literature and pray before hard conversations. Depth over noise.",
    lookingFor: "Someone faithful, funny in quiet ways, serious about family.",
    hobbies: "Reading, pottery, volunteering",
    scales: { loyalty: 8, honesty: 9, passion: 6, commitment: 8 },
    photoUrl: PHOTO.grace,
    plan: "paid",
  },
  {
    id: "cand_f_3",
    firstName: "Elena",
    age: 26,
    city: "Denver",
    gender: "female",
    profession: "Architect",
    aboutYou: "I design spaces and try to live with the same clarity — clean lines, honest materials.",
    lookingFor: "A partner who builds with intention and does not fear commitment.",
    hobbies: "Sketching, trail running, jazz",
    scales: { loyalty: 7, honesty: 8, passion: 8, commitment: 7 },
    photoUrl: PHOTO.elena,
    likedYou: true,
    plan: "free",
  },
  {
    id: "cand_f_4",
    firstName: "Hannah",
    age: 33,
    city: "Chicago",
    gender: "female",
    profession: "Counselor",
    aboutYou: "I listen for a living. Off the clock I want laughter, Scripture, and shared meals.",
    lookingFor: "Emotional honesty and spiritual maturity — not perfection.",
    hobbies: "Journaling, cycling, hosting dinner",
    scales: { loyalty: 9, honesty: 9, passion: 7, commitment: 9 },
    photoUrl: PHOTO.hannah,
    plan: "free",
  },
  {
    id: "cand_f_5",
    firstName: "Ruth",
    age: 29,
    city: "Seattle",
    gender: "female",
    profession: "Software engineer",
    aboutYou: "Calm under pressure. I care about craft, church community, and keeping Sundays holy.",
    lookingFor: "A man who is kind when no one is watching.",
    hobbies: "Climbing, board games, coffee roasting",
    scales: { loyalty: 8, honesty: 7, passion: 6, commitment: 8 },
    photoUrl: PHOTO.ruth,
    plan: "paid",
  },
  {
    id: "cand_f_6",
    firstName: "Lydia",
    age: 27,
    city: "Dallas",
    gender: "female",
    profession: "Worship leader",
    aboutYou: "I lead songs and try to live the words. Quiet coffee, loud Sundays.",
    lookingFor: "A man who prays when no one is scoring it.",
    hobbies: "Piano, running, hosting",
    scales: { loyalty: 9, honesty: 8, passion: 8, commitment: 9 },
    photoUrl: PHOTO.lydia,
    likedYou: true,
    plan: "vip",
  },
  {
    id: "cand_f_7",
    firstName: "Sarah",
    age: 30,
    city: "Boston",
    gender: "female",
    profession: "Librarian",
    aboutYou: "Stacks, liturgy, and long letters. I would rather be known than noticed.",
    lookingFor: "Patience, humour, and a shared table.",
    hobbies: "Reading, hiking, watercolours",
    scales: { loyalty: 8, honesty: 8, passion: 6, commitment: 8 },
    photoUrl: PHOTO.sarah,
    plan: "paid",
  },
  {
    id: "cand_f_8",
    firstName: "Naomi",
    age: 25,
    city: "Phoenix",
    gender: "female",
    profession: "Gardener",
    aboutYou: "I grow things slowly and keep the Sabbath. Heat does not scare me.",
    lookingFor: "Someone kind in the small hours.",
    hobbies: "Gardening, hymn nights, baking",
    scales: { loyalty: 8, honesty: 8, passion: 7, commitment: 8 },
    photoUrl: PHOTO.naomi,
    plan: "free",
  },
  {
    id: "cand_m_1",
    firstName: "Daniel",
    age: 30,
    city: "Austin",
    gender: "male",
    profession: "Carpenter",
    aboutYou: "I work with my hands and try to keep my word as carefully as my joints.",
    lookingFor: "A woman of faith who wants a home, not a highlight reel.",
    hobbies: "Woodworking, fishing, men’s group",
    scales: { loyalty: 9, honesty: 8, passion: 7, commitment: 9 },
    photoUrl: PHOTO.daniel,
    likedYou: true,
    plan: "vip",
  },
  {
    id: "cand_m_2",
    firstName: "Nathan",
    age: 34,
    city: "Atlanta",
    gender: "male",
    profession: "Pastor / writer",
    aboutYou: "I teach, write, and fail forward. Looking for a teammate, not an audience.",
    lookingFor: "Wisdom, warmth, and someone who prays with me.",
    hobbies: "Running, reading theology, cooking",
    scales: { loyalty: 8, honesty: 9, passion: 8, commitment: 8 },
    photoUrl: PHOTO.nathan,
    plan: "paid",
  },
  {
    id: "cand_m_3",
    firstName: "Caleb",
    age: 27,
    city: "Dallas",
    gender: "male",
    profession: "Paramedic",
    aboutYou: "Long shifts, short temper with injustice, deep loyalty to those I love.",
    lookingFor: "Steady faith and someone who values presence over polish.",
    hobbies: "Basketball, grilling, volunteering",
    scales: { loyalty: 9, honesty: 7, passion: 8, commitment: 8 },
    photoUrl: PHOTO.caleb,
    likedYou: true,
    plan: "free",
  },
  {
    id: "cand_m_4",
    firstName: "James",
    age: 32,
    city: "Portland",
    gender: "male",
    profession: "Farmer",
    aboutYou: "Seasons teach patience. I want a marriage that grows the same way — slow and true.",
    lookingFor: "A partner who loves the land, the Lord, and honest work.",
    hobbies: "Gardening, hymn nights, hiking",
    scales: { loyalty: 8, honesty: 8, passion: 6, commitment: 9 },
    photoUrl: PHOTO.james,
    plan: "free",
  },
  {
    id: "cand_m_5",
    firstName: "Isaac",
    age: 29,
    city: "Charlotte",
    gender: "male",
    profession: "Financial analyst",
    aboutYou: "Numbers by day, community by night. I take integrity seriously.",
    lookingFor: "Clarity, kindness, and shared direction toward family.",
    hobbies: "Chess, tennis, mentoring teens",
    scales: { loyalty: 7, honesty: 9, passion: 7, commitment: 8 },
    photoUrl: PHOTO.isaac,
    plan: "paid",
  },
  {
    id: "cand_m_6",
    firstName: "Peter",
    age: 31,
    city: "Denver",
    gender: "male",
    profession: "Youth pastor",
    aboutYou: "I spend weeknights with teenagers and mornings with Scripture.",
    lookingFor: "A woman who wants a home that is open and holy.",
    hobbies: "Hiking, mentoring, guitar",
    scales: { loyalty: 9, honesty: 8, passion: 8, commitment: 9 },
    photoUrl: PHOTO.peter,
    likedYou: true,
    plan: "vip",
  },
  {
    id: "cand_m_7",
    firstName: "Thomas",
    age: 28,
    city: "Minneapolis",
    gender: "male",
    profession: "Physical therapist",
    aboutYou: "I help people walk again. I want a marriage that does the same — repair, then joy.",
    lookingFor: "Warmth, clarity, and someone who laughs easily.",
    hobbies: "Tennis, reading, cooking",
    scales: { loyalty: 8, honesty: 8, passion: 7, commitment: 8 },
    photoUrl: PHOTO.thomas,
    plan: "paid",
  },
  {
    id: "cand_m_8",
    firstName: "Andrew",
    age: 33,
    city: "Boise",
    gender: "male",
    profession: "Cabinetmaker",
    aboutYou: "Measure twice. I want a life that is built, not browsed.",
    lookingFor: "Faith that shows up on ordinary Tuesdays.",
    hobbies: "Fishing, hiking, woodworking",
    scales: { loyalty: 8, honesty: 8, passion: 6, commitment: 9 },
    photoUrl: PHOTO.andrew,
    plan: "free",
  },
];

/**
 * Attach the questionnaire so the demo exercises the real two-stage model.
 * Without `answers` on both sides the engine silently falls back to the old
 * four-axis score and neither the gates nor the affinity axes ever run.
 */
/** Staggered join dates so FIFA tenure is individual, not identical. */
const JOINED_AT: Record<string, string> = {
  cand_f_1: "2025-10-18",
  cand_f_2: "2026-03-02",
  cand_f_3: "2026-06-01",
  cand_f_4: "2026-08-22",
  cand_f_5: "2026-07-01",
  cand_f_6: "2025-12-04",
  cand_f_7: "2026-02-14",
  cand_f_8: "2026-05-20",
  cand_m_1: "2025-11-01",
  cand_m_2: "2026-01-15",
  cand_m_3: "2026-08-01",
  cand_m_4: "2025-09-20",
  cand_m_5: "2026-04-10",
  cand_m_6: "2025-10-08",
  cand_m_7: "2026-03-18",
  cand_m_8: "2026-06-12",
};

export const DEMO_PROFILES: MatchProfile[] = RAW_PROFILES.map((p) => ({
  ...p,
  country: "US",
  preferredPartnerCountry: "any",
  answers: DEMO_ANSWERS[p.id],
  joinedAt: JOINED_AT[p.id],
}));
